import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { visibleStage } from '../constants/hidden-stages';
import { humanizeCode } from '../utils/run-format';
import { humanizeFieldName } from '../utils/run-score-fields';
import { useStageDestination } from '../utils/stage-destination-context';

/**
 * Display labels for the run vocabularies (stages, statuses, field names) from
 * the `catalog-v2-run` namespace, falling back to a humanised code so a value
 * the locale does not list yet still reads sensibly.
 */
export function useRunLabels() {
  const { t } = useTranslation('catalog-v2-run');
  // Ambient destination from the nearest `StageDestinationCtx` (set by RunShell / the experiment
  // detail page) — the fallback every call site gets for free when it passes no `destination`.
  const ambientDestination = useStageDestination();
  /**
   * The ONE place a stage code becomes display text. `destination` (an already-resolved name,
   * e.g. `catalogLabel(tCatalog, 'connector', code)`) makes DELIVERY read "Publish to Google
   * Merchant Center" instead of the plain "Publish". An explicit arg always wins; omitted, it
   * falls back to the ambient batch/experiment destination from context; still none, plain
   * "Publish" — never a guess.
   */
  const stageLabel = useCallback(
    (stage: string | null | undefined, destination?: string | null) => {
      if (!stage) return '';
      // A hidden stage (Image check) reads as the stage it folds into — never its own name.
      const shown = visibleStage(stage);
      const resolved = destination !== undefined ? destination : ambientDestination;
      if (shown === 'DELIVERY' && resolved) {
        return t('stages.DELIVERY_TO', { destination: resolved });
      }
      return t(`stages.${shown}`, { defaultValue: humanizeCode(shown) });
    },
    [t, ambientDestination],
  );
  const statusLabel = useCallback(
    (status: string | null | undefined) =>
      status ? t(`status.${status}`, { defaultValue: humanizeCode(status) }) : '',
    [t],
  );
  /** A product field / score name as words (G3): locale map first, then "AEO", "Google product category". */
  const fieldLabel = useCallback(
    (name: string) => t(`scoreDetails.field.${name}`, { defaultValue: humanizeFieldName(name) }),
    [t],
  );
  return { stageLabel, statusLabel, fieldLabel };
}
